/**
 * Inventory Alert Card Component
 */

import { motion } from 'framer-motion';
import { AlertCircle, AlertTriangle, Info, Zap, CheckCircle } from 'lucide-react';
import type { InventoryAlert } from '../../types/inventory';
import { AlertSeverity } from '../../types/inventory';

interface InventoryAlertCardProps {
  alert: InventoryAlert;
  onResolve?: (alertId: string) => void;
}

export function InventoryAlertCard({ alert, onResolve }: InventoryAlertCardProps) {
  const getSeverityStyles = (severity: AlertSeverity) => {
    switch (severity) {
      case AlertSeverity.CRITICAL:
        return {
          bg: 'bg-red-50 dark:bg-red-500/10',
          border: 'border-red-200 dark:border-red-500/20',
          icon: <AlertCircle size={18} className="text-red-500 dark:text-red-400" />,
          text: 'text-red-700 dark:text-red-400',
          badge: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400'
        };
      case AlertSeverity.HIGH:
        return {
          bg: 'bg-amber-50 dark:bg-amber-500/10',
          border: 'border-amber-200 dark:border-amber-500/20',
          icon: <AlertTriangle size={18} className="text-amber-500 dark:text-amber-400" />,
          text: 'text-amber-700 dark:text-amber-400',
          badge: 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400'
        };
      case AlertSeverity.MEDIUM:
        return {
          bg: 'bg-orange-50 dark:bg-orange-500/10',
          border: 'border-orange-200 dark:border-orange-500/20',
          icon: <Zap size={18} className="text-orange-500 dark:text-orange-400" />,
          text: 'text-orange-700 dark:text-orange-400',
          badge: 'bg-orange-100 dark:bg-orange-500/20 text-orange-700 dark:text-orange-400'
        };
      case AlertSeverity.LOW:
      default:
        return {
          bg: 'bg-blue-50 dark:bg-blue-500/10',
          border: 'border-blue-200 dark:border-blue-500/20',
          icon: <Info size={18} className="text-blue-500 dark:text-blue-400" />,
          text: 'text-blue-700 dark:text-blue-400',
          badge: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400'
        };
    }
  };

  const styles = getSeverityStyles(alert.severity);

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className={`${styles.bg} ${styles.border} border rounded-xl p-4 transition-all`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="mt-0.5">{styles.icon}</div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-semibold uppercase px-2 py-0.5 rounded-md ${styles.badge}`}>
                {alert.severity}
              </span>
              {alert.ingredientName && (
                <span className="text-xs text-gray-600 dark:text-white/50">• {alert.ingredientName}</span>
              )}
            </div>
            <p className={`text-sm ${styles.text}`}>{alert.message}</p>
            <p className="text-xs text-gray-500 dark:text-white/40 mt-2">
              {new Date(alert.createdAt).toLocaleString('it-IT')}
            </p>
          </div>
        </div>
        {!alert.isResolved && onResolve && (
          <button
            onClick={() => onResolve(alert.id)}
            className="ml-4 px-3 py-1.5 text-xs font-medium rounded-lg bg-white dark:bg-white/[0.1] text-gray-700 dark:text-white/70 hover:bg-gray-100 dark:hover:bg-white/[0.15] transition-colors flex items-center gap-1"
          >
            <CheckCircle size={14} />
            Risolvi
          </button>
        )}
      </div>
    </motion.div>
  );
}
